import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { useCompleteBlock, useGetLesson, useGetLessons, useGetSpace } from '@onesource/services';
import { createFileRoute, Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';

import { hasHTML } from '#/utils/has-html';

/**
 * Embed Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/embed/:contentId
 * Shows an embed content block (URL iframe or sanitized HTML embed code)
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/embed/$contentId',
)({
  component: EmbedContentRoute,
});

const sanitizeEmbedHtml = (raw: string) =>
  DOMPurify.sanitize(raw, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'width', 'height', 'sandbox', 'referrerpolicy'],
  });

function EmbedContentRoute() {
  const { lessonId, contentId, moduleId, id: spaceId } = Route.useParams();
  const theme = useTheme();

  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  const embedBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.EMBED,
  );

  // Embed is a view block: opening it completes it.
  const trackedRef = useRef(false);
  const { mutate: completeBlock } = useCompleteBlock();
  useEffect(() => {
    if (!trackedRef.current && embedBlock) {
      trackedRef.current = true;
      completeBlock(contentId);
    }
  }, [contentId, embedBlock, completeBlock]);

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!embedBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Content not found</Alert>
      </Box>
    );
  }

  const raw = embedBlock.body?.trim() ?? '';
  const isUrl = /^https?:\/\//i.test(raw);

  return (
    <Box>
      <Breadcrumbs
        sx={{
          mb: { xs: 2, md: 3 },
          fontFamily: '"Figtree", sans-serif',
          fontSize: { xs: 14, md: 16 },
          lineHeight: { xs: '20px', md: '22px' },
          fontWeight: 400,
          color: theme.palette.grey[600],
          '& .MuiBreadcrumbs-separator': { mx: 0.6 },
        }}
      >
        {module?.parent && (
          <Link
            to="/community-hub/spaces/$id"
            params={{ id: spaceId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(module.parent.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.parent.name) }} />
            </Typography>
          </Link>
        )}
        {module && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
          </Link>
        )}
        {findParentLesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            {findParentLesson?.title}
          </Link>
        )}
        {lesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId"
            params={{ id: spaceId, moduleId, lessonId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson?.title) }} />
          </Link>
        )}
        <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
          Embed
        </Typography>
      </Breadcrumbs>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          {embedBlock.title || 'Embed'}
        </Typography>
      </Box>

      <Box pb={4}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            maxWidth: 1100,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {raw ? (
            <Box
              sx={{
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'grey.100',
                '& iframe': {
                  display: 'block',
                  width: '100%',
                  minHeight: { xs: 320, md: 650 },
                  border: 0,
                },
              }}
            >
              {isUrl ? (
                <Box
                  component="iframe"
                  src={raw}
                  title={embedBlock.title || 'Embedded content'}
                  sx={{ width: '100%', height: { xs: 320, md: 650 }, border: 0 }}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  allowFullScreen
                />
              ) : (
                <Box dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(raw) }} />
              )}
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No embed content available.
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
